import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, KpiCard } from "@/components/kit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";
import { CHART } from "@/lib/helpers";
import { CalendarCheck, ClipboardList, CheckCircle2, AlertTriangle, Target, ShieldCheck, HandCoins, UserCog } from "lucide-react";
import { TOKEN_KEY } from "@/lib/api";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 0 })}`;
const API = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (path) => `${API}/api/files/${path}?token=${encodeURIComponent(localStorage.getItem(TOKEN_KEY) || "")}`;

export default function EmployeeProfile() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = ["founder", "admin"].includes(user?.role);
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState(params.get("emp") || (isAdmin ? "" : user?.employee_id) || "");

  useEffect(() => {
    const p = params.get("emp");
    if (p && p !== selected) setSelected(p);
  }, [params]); // eslint-disable-line

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => (await api.get("/employees")).data,
    enabled: isAdmin,
  });

  const empId = selected || user?.employee_id;
  const { data, isLoading } = useQuery({
    queryKey: ["profile-360", empId],
    queryFn: async () => (await api.get(`/employees/${empId}/profile`)).data,
    enabled: !!empId,
  });

  const isOwn = user?.employee_id && empId === user.employee_id;

  // pending profile change requests (admin)
  const { data: pcr = [] } = useQuery({
    queryKey: ["profile-change-requests"],
    queryFn: async () => (await api.get("/profile/change-requests")).data,
    enabled: isAdmin,
  });
  const pending = pcr.filter((r) => r.status === "Pending");
  const reviewPcr = useMutation({
    mutationFn: ({ id, action }) => api.post(`/profile/change-requests/${id}/review`, { action }),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ["profile-change-requests"] });
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["profile-360"] });
      toast.success(`Request ${v.action === "approve" ? "approved" : "rejected"}`);
    },
  });

  // self-service request form
  const [draft, setDraft] = useState({ phone: "", emergency_contact: "", blood_group: "" });
  const submitChange = useMutation({
    mutationFn: () => api.post("/profile/change-request", draft),
    onSuccess: () => {
      setDraft({ phone: "", emergency_contact: "", blood_group: "" });
      qc.invalidateQueries({ queryKey: ["profile-change-requests"] });
      toast.success("Update request sent to the Head for approval.");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not submit request."),
  });

  const changeEmp = (v) => { setSelected(v); setParams({ emp: v }); };

  return (
    <div data-testid="employee-profile-page">
      <PageHeader title="Employee 360°" subtitle="Attendance, tasks, quality, loans & payslips in one view">
        {isAdmin && (
          <Select value={selected} onValueChange={changeEmp}>
            <SelectTrigger className="w-64" data-testid="profile-employee-select">
              <SelectValue placeholder="Select an employee" />
            </SelectTrigger>
            <SelectContent>
              {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name} ({e.employee_id})</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </PageHeader>

      {isAdmin && pending.length > 0 && (
        <SectionCard title={`Pending Profile Change Requests (${pending.length})`} testId="pcr-panel" className="mb-6">
          <div className="space-y-3">
            {pending.map((r) => (
              <div key={r.request_id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-3" data-testid={`pcr-${r.request_id}`}>
                <div className="text-sm">
                  <span className="font-medium text-navy">{r.name}</span>
                  <span className="text-slate-400"> ({r.employee_id})</span>
                  <div className="text-xs text-slate-600">
                    {Object.entries(r.fields).map(([k, v]) => <span key={k} className="mr-3"><b>{k.replace(/_/g, " ")}:</b> {v}</span>)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => reviewPcr.mutate({ id: r.request_id, action: "approve" })} data-testid={`pcr-approve-${r.request_id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white">Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => reviewPcr.mutate({ id: r.request_id, action: "reject" })} data-testid={`pcr-reject-${r.request_id}`} className="border-red-200 text-red-600 hover:bg-red-50">Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {!empId ? (
        <SectionCard><p className="py-10 text-center text-sm text-slate-400">Select an employee to view their 360° profile.</p></SectionCard>
      ) : isLoading || !data ? (
        <Skeleton className="h-96 rounded-lg" />
      ) : (
        <>
          <SectionCard testId="profile-header" className="mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-navy text-xl font-heading font-bold text-gold">
                {data.employee.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-xl font-bold text-navy">{data.employee.name}</h2>
                <p className="text-sm text-slate-500">{data.employee.designation || "Data Entry Operator"} · {data.employee.employee_id}</p>
                <p className="text-xs text-slate-400">{data.employee.email} {data.employee.phone ? `· ${data.employee.phone}` : ""}</p>
              </div>
              <Badge className="bg-gold/15 text-gold-dark border-gold/30">{data.employee.full_time ? "Full-time" : "Part-time"}</Badge>
            </div>
          </SectionCard>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <KpiCard testId="p-attendance" label="Attendance (30d)" value={`${data.attendance_pct}%`} icon={CalendarCheck} accent="green" />
            <KpiCard testId="p-assigned" label="Tasks Assigned" value={data.tasks_assigned} icon={ClipboardList} />
            <KpiCard testId="p-completed" label="Completed" value={data.tasks_completed} icon={CheckCircle2} accent="green" />
            <KpiCard testId="p-overdue" label="Overdue" value={data.tasks_overdue} icon={AlertTriangle} accent="red" />
            <KpiCard testId="p-accuracy" label="Avg Accuracy" value={`${data.avg_accuracy}%`} icon={Target} accent="gold" />
            <KpiCard testId="p-quality" label="Avg Quality" value={data.avg_quality} icon={ShieldCheck} accent="gold" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <SectionCard title="Quality Trend (14 days)" testId="p-quality-chart">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="quality" name="Quality" stroke={CHART.gold} strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </SectionCard>
            <SectionCard title="Task Throughput (14 days)" testId="p-throughput-chart">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip />
                  <Bar dataKey="completed" name="Completed" fill={CHART.green} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </SectionCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SectionCard title="Active Loan" testId="p-loan">
              {data.loan ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Principal</span><span className="font-semibold text-navy">{inr(data.loan.principal)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Monthly EMI</span><span className="font-semibold text-navy">{inr(data.loan.emi)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Outstanding</span><span className="font-semibold text-red-600">{inr(data.loan.outstanding)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">EMIs paid</span><span className="font-medium">{data.loan.paid_emis} / {data.loan.tenure}</span></div>
                  <div className="text-xs text-slate-400">Ref: {data.loan.agreement_number}</div>
                </div>
              ) : (
                <div className="flex items-center gap-2 py-6 text-sm text-slate-400"><HandCoins className="h-5 w-5" /> No active loan.</div>
              )}
            </SectionCard>
            <SectionCard title="Payslip History" testId="p-payslips">
              {data.payslips.length ? (
                <Table>
                  <TableHeader><TableRow><TableHead>Month</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Net</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {data.payslips.map((p) => (
                      <TableRow key={p.payslip_id}><TableCell>{p.month}</TableCell><TableCell className="text-right">{inr(p.gross_earnings)}</TableCell><TableCell className="text-right font-semibold text-emerald-700">{inr(p.net_pay)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : <p className="py-6 text-center text-sm text-slate-400">No payslips yet.</p>}
            </SectionCard>
          </div>

          <SectionCard title="Face Re-check History" testId="p-face-checks" className="mt-6">
            {data.face_checks?.length ? (
              <div className="max-h-80 overflow-y-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Result</TableHead><TableHead className="text-right">Proof</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {data.face_checks.map((f) => (
                      <TableRow key={f.check_id} data-testid={`face-check-${f.check_id}`}>
                        <TableCell className="text-sm">{f.date} · {f.time}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={f.result === "fail" ? "bg-red-100 text-red-700 border-red-200" : "bg-emerald-100 text-emerald-800 border-emerald-200"}>
                            {f.result === "fail" ? "Failed" : "Passed"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {f.snapshot_path ? (
                            <a href={fileUrl(f.snapshot_path)} target="_blank" rel="noreferrer" data-testid={`face-snap-${f.check_id}`} className="text-xs font-medium text-gold-dark hover:underline">View snapshot</a>
                          ) : <span className="text-xs text-slate-400">—</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : <p className="py-6 text-center text-sm text-slate-400">No face re-checks recorded yet.</p>}
          </SectionCard>

          {isOwn && (
            <SectionCard title="Update My Details" testId="self-service" className="mt-6">
              <p className="mb-4 text-xs text-slate-500">Changes are sent to the Head for approval before they take effect.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><Label>Phone</Label><Input data-testid="ss-phone" className="mt-1" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder={data.employee.phone || "New phone"} /></div>
                <div><Label>Emergency Contact</Label><Input data-testid="ss-emergency" className="mt-1" value={draft.emergency_contact} onChange={(e) => setDraft({ ...draft, emergency_contact: e.target.value })} placeholder={data.employee.emergency_contact || "New emergency contact"} /></div>
                <div><Label>Blood Group</Label><Input data-testid="ss-blood" className="mt-1" value={draft.blood_group} onChange={(e) => setDraft({ ...draft, blood_group: e.target.value })} placeholder={data.employee.blood_group || "e.g. O+"} /></div>
              </div>
              <Button
                className="mt-4 rounded-lg bg-navy text-white hover:bg-navy-900"
                data-testid="ss-submit"
                disabled={submitChange.isPending || (!draft.phone && !draft.emergency_contact && !draft.blood_group)}
                onClick={() => submitChange.mutate()}
              >
                <UserCog className="mr-2 h-4 w-4" /> Request Update
              </Button>
            </SectionCard>
          )}
        </>
      )}
    </div>
  );
}
